import { darken, lighten, transparentize } from 'polished';

export type ThemeMode = 'light' | 'dark';

const baseTheme = {
    text: {
        size: {
            xxsmall: '1rem',
            xsmall: '1.2rem',
            small: '1.3rem',
            medium: '1.4rem',
            large: '1.6rem',
            xlarge: '2rem',
            xxlarge: '2.5rem',
            xxxlarge: '4rem',
        },
        type: {
            bold: 'HKGrotesk-Bold',
            medium: 'HKGrotesk-Medium',
        },
        font: {
            regular: 'HKGrotesk-Regular',
            medium: 'HKGrotesk-Medium',
            bold: 'HKGrotesk-Bold',
        },
    },
    icon: {
        size: {
            xxsmall: '8',
            xsmall: '12',
            small: '16',
            normal: '24',
            large: '32',
            xlarge: '40',
            xxlarge: '48',
        },
    },
    timeout: {
        short: 300,
        medium: 600,
        long: 1000,
        veryLong: 6000,
    },
    layout: {
        padding: {
            xxsmall: '0.3rem',
            xsmall: '0.5rem',
            small: '1rem',
            medium: '1.5rem',
            large: '2rem',
            xxl: '3rem',
        },
        margin: {
            xxsmall: '0.3rem',
            xsmall: '0.5rem',
            small: '1rem',
            xmedium: '1.25rem',
            medium: '1.5rem',
            large: '2rem',
            xLarge: '3rem',
        },
        gap: {
            xxsmall: '0.1rem',
            xsmall: '0.5rem',
            small: '1rem',
            medium: '2rem',
            large: '3rem',
            xLarge: '4rem',
        },
    },
};

export const walleyLightTheme = {
    ...baseTheme,
    mode: 'light' as ThemeMode,
    background: '#fff',
    colors: {
        primary: '#3D388B',
        lightPrimary: transparentize(0.9, '#3D388B'),
        mediumPrimary: transparentize(0.7, '#3D388B'),
        hoverPrimary: transparentize(0.2, '#3D388B'),
        otherPrimary: '#7374CD',
        success: '#41A664',
        accent: lighten(0.57, '#3D388B'),
        accent2: '#3DA8B8',
        lightAccent2: transparentize(0.9, '#3DA8B8'),
        lighterAccent2: '#DEF1F4',
        otherAccent2: '#1A90A0',
        lighterAccent3: '#FDF3E1',
        accent3: '#F2B94C',
        otherAccent3: '#A67700',
        accent4: '#EAB3B5',
        otherAccent4: '#A37173',
        accent5: '#F94D3D',
        otherAccent5: '#E33528',
        error: '#F02C2C',
        info: '#5bc0de',
        warning: '#f0ad4e',
        disabled: '#d6d4d4',
        light: '#ffffff',
        medium: '#f4f4f4',
        dark: '#C7C7C7',
        veryDark: '#737272',
        black: 'black',
        defaultIcon: '#b9b9b9',
        payment: '#f5f5f5',
        background: '#f9fcff',
        shadow: 'rgba(183, 183, 183, 0.2)',
        overlay: 'rgba(0, 0, 0, 0.6)',
        subtle: {
            regular: '#a3a3a4',
            light: '#eef0f3',
            lightHover: '#e8eaed',
            lighter: '#fdfdfd',
        },
        text: {
            primary: '#333',
            secondary: '#888',
            subtleSecondary: '#b5b5b5',
            subtle: '#efefef',
        },
        status: {
            activated: '#0ebd0e',
            partActivated: '#3087ff',
            notActivated: '#ffbe2c',
            closed: '#888888',
            onHold: '#760ebd',
            paid: '#0ebd0e',
            partiallyPaid: '#3087ff',
            notPaid: '#ffbe2c',
            canceled: '#888888',
            default: 'gray',
        },
        httpStatusGroup: {
            informational: '#322E73',
            success: '#41A664',
            redirectional: '#F29F00',
            clientError: '#F02C2C',
            serverError: '#8B000A',
            default: 'gray',
        },
        apiSecret: {
            status: {
                expired: '#E34234',
                expiresSoon: '#ffbe2c',
                activate: '#0ebd0e',
            },
        },
        toast: {
            info: '#ededf8',
        },
        snackbar: {
            normal: '#ededf8',
            error: {
                background: '#ff8c8c',
                text: '#fff',
            },
        },
        tag: {
            default: '#f7f7f7',
        },
        card: {
            border: '#e3e3e3',
        },
        border: '#e4e4e4',
        input: {
            border: '#bababa',
        },
        navBar: {
            background: '#040e28',
            shineThrough: '#646d8280',
            active: '#8c96af80',
            foreground: '#fff',
        },
        table: {
            row: '#f6f8fb',
            modifiedRow: '#d7d5ee',
        },
        button: {
            light: '#e2e4e7',
            medium: '#DEDEDE',
            primary: {
                text: '#fff',
            },
            secondary: {
                color: '#fff',
                border: '#bfd4d9',
                hover: '#F9F9F9',
                text: '#000000',
            },
        },
        skeletonLoader: {
            primary: '#ebebeb',
            secondary: '#f3f2f2',
        },
        pendingUsers: {
            status: {
                expired: '#E34234',
            },
        },
    },
};

export const walleyDarkTheme = {
    ...baseTheme,
    mode: 'dark' as ThemeMode,
    background: '#121212',
    colors: {
        primary: '#7374CD',
        lightPrimary: transparentize(0.85, '#7374CD'),
        mediumPrimary: transparentize(0.6, '#7374CD'),
        hoverPrimary: transparentize(0.2, '#7374CD'),
        otherPrimary: '#9596E0',
        success: '#4CC474',
        accent: transparentize(0.85, '#7374CD'),
        accent2: '#4DB9C9',
        lightAccent2: transparentize(0.85, '#4DB9C9'),
        lighterAccent2: '#1A3A3F',
        otherAccent2: '#2AA1B1',
        lighterAccent3: '#2D2510',
        accent3: '#F2B94C',
        otherAccent3: '#C89000',
        accent4: '#C49496',
        otherAccent4: '#8A5759',
        accent5: '#F94D3D',
        otherAccent5: '#E33528',
        error: '#FF5252',
        info: '#64B5F6',
        warning: '#FFB74D',
        disabled: '#4a4a4a',
        light: '#1e1e1e',
        medium: '#2d2d2d',
        dark: '#555555',
        veryDark: '#999999',
        black: '#ffffff',
        defaultIcon: '#666666',
        payment: '#252525',
        background: '#181818',
        shadow: 'rgba(0, 0, 0, 0.4)',
        overlay: 'rgba(0, 0, 0, 0.6)',
        subtle: {
            regular: '#888888',
            light: '#242424',
            lightHover: '#2d2d2d',
            lighter: '#1a1a1a',
        },
        text: {
            primary: '#e0e0e0',
            secondary: '#a0a0a0',
            subtleSecondary: '#707070',
            subtle: '#3a3a3a',
        },
        status: {
            activated: '#4CC474',
            partActivated: '#5C9FFF',
            notActivated: '#FFD54F',
            closed: '#888888',
            onHold: '#A855F7',
            paid: '#4CC474',
            partiallyPaid: '#5C9FFF',
            notPaid: '#FFD54F',
            canceled: '#888888',
            default: 'gray',
        },
        httpStatusGroup: {
            informational: '#7374CD',
            success: '#4CC474',
            redirectional: '#FFB74D',
            clientError: '#FF5252',
            serverError: '#FF1744',
            default: 'gray',
        },
        apiSecret: {
            status: {
                expired: '#FF5252',
                expiresSoon: '#FFD54F',
                activate: '#4CC474',
            },
        },
        toast: {
            info: '#2a2a3a',
        },
        snackbar: {
            normal: '#2a2a3a',
            error: {
                background: '#b71c1c',
                text: '#fff',
            },
        },
        tag: {
            default: '#2d2d2d',
        },
        card: {
            border: '#3a3a3a',
        },
        border: '#3a3a3a',
        input: {
            border: '#555555',
        },
        navBar: {
            background: '#0a1628',
            shineThrough: '#4a5570',
            active: '#6c7690',
            foreground: '#fff',
        },
        table: {
            row: '#1e1e1e',
            modifiedRow: '#2a2a4a',
        },
        button: {
            light: '#2d2d2d',
            medium: '#3a3a3a',
            primary: {
                text: '#fff',
            },
            secondary: {
                color: '#2d2d2d',
                border: '#4a5a5f',
                hover: '#3a3a3a',
                text: '#e0e0e0',
            },
        },
        skeletonLoader: {
            primary: '#2d2d2d',
            secondary: '#3a3a3a',
        },
        pendingUsers: {
            status: {
                expired: '#FF5252',
            },
        },
    },
};

export const themeForTests = walleyLightTheme;
